/* ══════════════════════════════════════════════════════════════════════════
   KÖZÖS MAGYAR SZÁMFORMÁZÁS (3a) — minden kijelzett szám ezen megy át
   ─────────────────────────────────────────────────────────────────────────
   - tizedesvessző, ezres tagolás nem törhető szóközzel, négyjegyű számoknál
     is (useGrouping: 'always'): 4 183,00 $
   - a pénznem a szám után: 82 774 $
   - nagy számok: 2,79 billió $ / szűken 2 790 Mrd $, 58,1 Mrd $, 312 M $
     (a „B” rövidítést nem használjuk: magyarul billiót jelentene)
   - 1 $ alatt 4 értékes jegy: 0,08584 $, 0,0001235 $
   - százalék mindig előjellel, tipográfiai mínusszal (U+2212): +3,45%, −2,12%
   - dátum/idő Europe/Budapest: 2026. okt. 10. 18:43
   Csak megjelenítésre: beviteli mezők értékéhez ne használd (a vessző miatt).
   Tesztek: fmt.test.js
   ══════════════════════════════════════════════════════════════════════════ */

export const NBSP = ' ';
export const MINUS = '−';
const TZ = 'Europe/Budapest';
const DASH = '—';

const cache = new Map();
function nf(min, max, extra = {}) {
  const key = `${min}|${max}|${JSON.stringify(extra)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.NumberFormat('hu-HU', { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping: 'always', ...extra });
    cache.set(key, f);
  }
  return f;
}

const ok = (n) => typeof n === 'number' && Number.isFinite(n);
// hu-HU uses U+00A0 for grouping, but some engines emit U+202F; keep one kind,
// and the typographic minus for negatives.
const clean = (s) => s.replace(/\u202F/g, NBSP).replace(/^-/, MINUS);

/** Plain number: fmtNum(4183) → "4 183", fmtNum(4183, 2) → "4 183,00". */
export function fmtNum(n, decimals = 0, minDecimals = decimals) {
  if (!ok(n)) return DASH;
  return clean(nf(minDecimals, decimals).format(n));
}

/** Decimals for a price: ≥ 1 → fixed; < 1 → 4 significant digits. */
function priceParts(p, dec) {
  const a = Math.abs(p);
  if (a === 0) return { min: 2, max: 2 };
  if (a >= 1) return { min: dec ?? 2, max: dec ?? 2 };
  // 4 significant digits: 0,08584 / 0,0001235
  const lead = Math.floor(-Math.log10(a)); // zeros after the decimal point
  const max = Math.min(lead + 4, 12);
  return { min: 0, max };
}

/** Number only (no currency) with price precision: 82774 → "82 774,00". */
export function fmtPriceNum(p, decimals) {
  if (!ok(p)) return DASH;
  const { min, max } = priceParts(p, decimals);
  return clean(nf(min, max).format(p));
}

/**
 * Price with "$" after it.
 *   fmtUsd(82774.3) → "82 774,30 $"   fmtUsd(82774.3, 0) → "82 774 $"
 *   fmtUsd(0.085843) → "0,08584 $"    fmtUsd(0.00012345) → "0,0001235 $"
 */
export function fmtUsd(p, decimals) {
  if (!ok(p)) return DASH;
  return `${fmtPriceNum(p, decimals)}${NBSP}$`;
}

/** Price with sensible display decimals: ≥ 1000 → 0, ≥ 1 → 2, < 1 → 4 sign. digits. */
export function fmtPrice(p) {
  if (!ok(p)) return DASH;
  const a = Math.abs(p);
  return fmtUsd(p, a >= 1000 ? 0 : a >= 1 ? 2 : undefined);
}

/**
 * Large amounts.
 *   fmtCompact(2.79e12) → "2,79 billió $"   fmtCompact(2.79e12, { narrow: true }) → "2 790 Mrd $"
 *   fmtCompact(58.1e9) → "58,1 Mrd $"       fmtCompact(312e6) → "312 M $"
 * `unit: ''` drops the currency (e.g. supply).
 */
export function fmtCompact(n, { narrow = false, unit = '$', decimals } = {}) {
  if (!ok(n)) return DASH;
  const a = Math.abs(n);
  const u = unit ? `${NBSP}${unit}` : '';
  const d = (v) => decimals ?? (v >= 100 ? 0 : v >= 10 ? 1 : 2);
  if (a >= 1e12 && !narrow) { const v = n / 1e12; return `${fmtNum(v, d(Math.abs(v)), 0)}${NBSP}billió${u}`; }
  if (a >= 1e9) { const v = n / 1e9; return `${fmtNum(v, d(Math.abs(v)), 0)}${NBSP}Mrd${u}`; }
  if (a >= 1e6) { const v = n / 1e6; return `${fmtNum(v, d(Math.abs(v)), 0)}${NBSP}M${u}`; }
  if (a >= 1e3) return `${fmtNum(n, 0)}${u}`;
  return `${fmtNum(n, a >= 1 ? 2 : 4, 0)}${u}`;
}

/** Percent, always signed: +3,45% / −2,12% / 0,00%. `p` is already in percent. */
export function fmtPct(p, decimals = 2) {
  if (!ok(p)) return DASH;
  const s = nf(decimals, decimals).format(Math.abs(p));
  const sign = p > 0 ? '+' : p < 0 && Number(s.replace(/\s/g, '').replace(',', '.')) !== 0 ? MINUS : '';
  return `${sign}${clean(s)}%`;
}

/** Percent without forced sign (shares, dominance): 59,6% */
export function fmtShare(p, decimals = 1) {
  if (!ok(p)) return DASH;
  return `${fmtNum(p, decimals)}%`;
}

const dtf = new Map();
function df(opts) {
  const key = JSON.stringify(opts);
  let f = dtf.get(key);
  if (!f) { f = new Intl.DateTimeFormat('hu-HU', { timeZone: TZ, ...opts }); dtf.set(key, f); }
  return f;
}
const toDate = (t) => (t instanceof Date ? t : new Date(t));
const valid = (d) => d instanceof Date && !Number.isNaN(d.getTime());

/** 2026. okt. 10. 18:43 */
export function fmtDateTime(t) {
  const d = toDate(t); if (!valid(d)) return DASH;
  const parts = Object.fromEntries(df({ year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d).map(p => [p.type, p.value]));
  return `${parts.year}. ${parts.month.replace(/\.$/, '')}. ${parts.day}. ${parts.hour}:${parts.minute}`;
}

/** okt. 14. kedd 14:30 (közelgő eseményekhez) */
export function fmtEventTime(t) {
  const d = toDate(t); if (!valid(d)) return DASH;
  const parts = Object.fromEntries(df({ month: 'short', day: 'numeric', weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d).map(p => [p.type, p.value]));
  return `${parts.month.replace(/\.$/, '')}. ${parts.day}. ${parts.weekday} ${parts.hour}:${parts.minute}`;
}

/** 14:32:05 */
export function fmtClock(t, seconds = true) {
  const d = toDate(t); if (!valid(d)) return DASH;
  return df({ hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}), hour12: false }).format(d);
}
